import type React from 'react';
import { twMerge } from 'tailwind-merge';

import { Box } from 'ui/box';

import MessageInput, { type MessageInputHandle } from './message-input';
import MessageInputNotice from './message-input-notice';

import type { ActiveInputPart } from '../../hooks/use-active-input-part';
import type { EditingMessage } from '../../hooks/use-mark-chat';

interface ChatComposerProps {
  isBlocked: boolean;
  limitReachedMessage: string;
  activeInputPart: ActiveInputPart | null;
  onSubmitInputPart: (text: string) => void;
  onDismissInputPart: () => void;
  onSendMessage: (message: string) => void;
  isInputDisabled: boolean;
  editingMessage: EditingMessage | null;
  onSubmitEditedMessage: (content: string) => void;
  onCancelEditingMessage: () => void;
  inputRef?: React.Ref<MessageInputHandle>;
  inputWrapperProps?: React.HTMLAttributes<HTMLDivElement>;
}

const ChatComposer = ({
  isBlocked,
  limitReachedMessage,
  activeInputPart,
  onSubmitInputPart,
  onDismissInputPart,
  onSendMessage,
  isInputDisabled,
  editingMessage,
  onSubmitEditedMessage,
  onCancelEditingMessage,
  inputRef,
  inputWrapperProps,
}: ChatComposerProps): React.JSX.Element => {
  if (isBlocked) {
    return (
      <Box sx={{ maxWidth: '800px', mx: 'auto', width: '100%' }}>
        <MessageInputNotice notice={limitReachedMessage} />
      </Box>
    );
  }

  if (activeInputPart) {
    return (
      <Box sx={{ maxWidth: '800px', mx: 'auto', width: '100%' }}>
        <activeInputPart.Component
          data={activeInputPart.data}
          onSubmit={onSubmitInputPart}
          onDismiss={onDismissInputPart}
        />
      </Box>
    );
  }

  const { className: wrapperClassName, ...restWrapperProps } =
    inputWrapperProps ?? {};

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', width: '100%' }}>
      <div
        {...restWrapperProps}
        className={twMerge('w-full', wrapperClassName)}
      >
        <MessageInput
          key={editingMessage?.id ?? 'compose'}
          ref={inputRef}
          onSendMessage={editingMessage ? onSubmitEditedMessage : onSendMessage}
          disabled={isInputDisabled}
          isEditing={!!editingMessage}
          initialValue={editingMessage?.content}
          onCancelEditing={onCancelEditingMessage}
        />
      </div>
    </Box>
  );
};

export default ChatComposer;
